import api from './api';

/**
 * Case Management Service
 * Handles all API calls related to case management for member outreach
 */
class CaseService {
  /**
   * Get all cases with optional filtering and pagination
   * @param {Object} params - Query parameters
   * @param {string} params.status - Filter by status (pending-call, call-attempted, etc.)
   * @param {string} params.priority - Filter by priority (urgent, high, medium, low)
   * @param {string} params.riskLevel - Filter by risk level (critical, high, medium, low, minimal)
   * @param {string} params.assignedTo - Filter by assigned staff member
   * @param {string} params.search - Search member name or ID
   * @param {string} params.startDate - Filter by start date (ISO string)
   * @param {string} params.endDate - Filter by end date (ISO string)
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} Cases with pagination info
   */
  async getCases(params = {}) {
    try {
      const response = await api.get('/cases', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching cases:', error);
      throw error;
    }
  }

  /**
   * Get a single case by ID with full member data
   * @param {string} caseId - Case ID
   * @returns {Promise<Object>} Case with populated member information
   */
  async getCaseById(caseId) {
    try {
      const response = await api.get(`/cases/${caseId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching case ${caseId}:`, error);
      throw error;
    }
  }

  /**
   * Get case statistics for dashboard
   * @returns {Promise<Object>} Statistics including counts by status, priority, etc.
   */
  async getCaseStats() {
    try {
      const response = await api.get('/cases/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching case stats:', error);
      throw error;
    }
  }

  /**
   * Search all members for case creation
   * @param {Object} params - Search parameters
   * @param {string} params.search - Text search (name, ID, email, phone)
   * @param {string} params.riskLevel - Filter by risk level
   * @param {number} params.riskScoreMin - Minimum risk score
   * @param {number} params.riskScoreMax - Maximum risk score
   * @param {boolean} params.hasResults - Filter members with screening data
   * @param {boolean} params.needsOutreach - Filter high-risk members (score >= 50)
   * @param {string} params.eventId - Filter by event
   * @param {string} params.dateFrom - Start date
   * @param {string} params.dateTo - End date
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise<Object>} Members with calculated risk scores
   */
  async searchMembers(params = {}) {
    try {
      const response = await api.get('/cases/search-members', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching members:', error);
      throw error;
    }
  }

  /**
   * Get events that have high-risk members needing outreach, with contacted
   * and responded counts per event.
   * @param {Object} params - e.g. { engagement: true } to include contacted/responded counts
   * @returns {Promise<Object>} { data: { events: [...] } }
   */
  async getOutreachEvents(params = {}) {
    try {
      const response = await api.get('/cases/outreach/events', params);
      return response;
    } catch (error) {
      console.error('Error loading outreach events:', error);
      throw error;
    }
  }

  /**
   * Get high-risk members screened at a given event, with outreach status.
   * @param {string} eventKey - Event grouping key
   * @param {Object} params - Optional filters (e.g. responded: 'true'|'false')
   * @returns {Promise<Object>} { data: { event, members } }
   */
  async getEventOutreachMembers(eventKey, params = {}) {
    try {
      const response = await api.get(
        `/cases/outreach/events/${encodeURIComponent(eventKey)}/members`,
        params
      );
      return response;
    } catch (error) {
      console.error('Error loading event outreach members:', error);
      throw error;
    }
  }

  /**
   * Every email the platform has sent (newest first).
   * @param {Object} params - search, type, status, page, pageSize
   * @returns {Promise<Object>} { data: { items, total, page, pageSize, typeCounts } }
   */
  async getEmailLog(params = {}) {
    try {
      const response = await api.get('/cases/outreach/email-log', params);
      return response;
    } catch (error) {
      console.error('Error loading email log:', error);
      throw error;
    }
  }

  /**
   * Get a single member's outreach status + activity timeline.
   * @param {string} patientId
   * @returns {Promise<Object>} { data: { name, email, phone, status, activity } }
   */
  async getMemberOutreach(patientId) {
    try {
      const response = await api.get(`/cases/outreach/member/${encodeURIComponent(patientId)}`);
      return response;
    } catch (error) {
      console.error('Error loading member outreach:', error);
      throw error;
    }
  }

  /**
   * Send email or text outreach to selected high-risk members.
   * @param {Object} payload
   * @param {'email'|'text'} payload.method - Outreach channel
   * @param {string} payload.eventKey - Event grouping key
   * @param {string} payload.eventName - Event name (for case context)
   * @param {string[]} payload.patientIds - Members to contact
   * @param {string} payload.subject - Email subject (email only)
   * @param {string} payload.message - Email body or SMS text
   * @returns {Promise<Object>} { summary, results }
   */
  async sendOutreach(payload) {
    try {
      const response = await api.post('/cases/outreach/send', payload);
      return response;
    } catch (error) {
      console.error('Error sending outreach:', error);
      throw error;
    }
  }

  /**
   * Auto-create a case for a high-risk member
   * @param {Object} data - Member and risk data
   * @param {string} data.patientId - Member ID
   * @param {string} data.visitId - Visit ID
   * @param {string} data.eventId - Event ID
   * @param {number} data.riskScore - Risk score (raw 0-300)
   * @param {number} data.normalizedRiskScore - Normalized risk score (0-100)
   * @param {string} data.riskLevel - Risk level (critical, high, etc.)
   * @param {Array} data.criticalFindings - Array of critical findings
   * @param {Object} data.screeningData - Full screening data
   * @returns {Promise<Object>} Created case or existing case if duplicate
   */
  async autoCreateCase(data) {
    try {
      const response = await api.post('/cases/auto-create', data);
      return response.data;
    } catch (error) {
      console.error('Error auto-creating case:', error);
      throw error;
    }
  }

  /**
   * Update a case
   * @param {string} caseId - Case ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated case
   */
  async updateCase(caseId, updates) {
    try {
      const response = await api.patch(`/cases/${caseId}`, updates);
      return response.data;
    } catch (error) {
      console.error(`Error updating case ${caseId}:`, error);
      throw error;
    }
  }

  /**
   * Add a note to a case
   * @param {string} caseId - Case ID
   * @param {Object} noteData - Note data
   * @param {string} noteData.text - Note text
   * @param {string} noteData.noteType - Type: general, call-log, follow-up, resolution
   * @param {string} noteData.createdBy - Staff member name
   * @returns {Promise<Object>} Updated case with new note
   */
  async addCaseNote(caseId, noteData) {
    try {
      const response = await api.post(`/cases/${caseId}/notes`, noteData);
      return response.data;
    } catch (error) {
      console.error(`Error adding note to case ${caseId}:`, error);
      throw error;
    }
  }

  /**
   * Record a call attempt for a case
   * @param {string} caseId - Case ID
   * @param {Object} callData - Call attempt data
   * @param {string} callData.outcome - Outcome: completed, no-answer, voicemail, wrong-number, disconnected
   * @param {string} callData.notes - Call notes
   * @param {Date} callData.nextScheduledCall - Next call date if needed
   * @param {string} callData.voiceCallId - VoiceCall record ID if call connected
   * @returns {Promise<Object>} Updated case
   */
  async recordCallAttempt(caseId, callData) {
    try {
      const response = await api.post(`/cases/${caseId}/call-attempt`, callData);
      return response.data;
    } catch (error) {
      console.error(`Error recording call attempt for case ${caseId}:`, error);
      throw error;
    }
  }

  /**
   * Schedule a follow-up for a case
   * @param {string} caseId - Case ID
   * @param {Object} followUpData - Follow-up data
   * @param {Date} followUpData.scheduledDate - Follow-up date
   * @param {string} followUpData.reason - Reason for follow-up
   * @returns {Promise<Object>} Updated case
   */
  async scheduleFollowUp(caseId, followUpData) {
    try {
      const response = await api.post(`/cases/${caseId}/follow-up`, followUpData);
      return response.data;
    } catch (error) {
      console.error(`Error scheduling follow-up for case ${caseId}:`, error);
      throw error;
    }
  }

  /**
   * Resolve a case
   * @param {string} caseId - Case ID
   * @param {Object} resolutionData - Resolution data
   * @param {string} resolutionData.outcome - Outcome: member-contacted, referral-made, declined, etc.
   * @param {string} resolutionData.notes - Resolution notes
   * @returns {Promise<Object>} Resolved case
   */
  async resolveCase(caseId, resolutionData) {
    try {
      const response = await api.post(`/cases/${caseId}/resolve`, resolutionData);
      return response.data;
    } catch (error) {
      console.error(`Error resolving case ${caseId}:`, error);
      throw error;
    }
  }

  /**
   * Assign a case to a staff member
   * @param {string} caseId - Case ID
   * @param {Object} assignmentData - Assignment data
   * @param {string} assignmentData.userId - Staff user ID
   * @param {string} assignmentData.staffName - Staff name
   * @param {string} assignmentData.department - Department
   * @returns {Promise<Object>} Updated case
   */
  async assignCase(caseId, assignmentData) {
    try {
      const response = await api.patch(`/cases/${caseId}`, {
        assignedTo: assignmentData
      });
      return response.data;
    } catch (error) {
      console.error(`Error assigning case ${caseId}:`, error);
      throw error;
    }
  }

  /**
   * Update case status
   * @param {string} caseId - Case ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated case
   */
  async updateStatus(caseId, status) {
    try {
      const response = await api.patch(`/cases/${caseId}`, { status });
      return response.data;
    } catch (error) {
      console.error(`Error updating case status ${caseId}:`, error);
      throw error;
    }
  }

  /**
   * Update case priority
   * @param {string} caseId - Case ID
   * @param {string} priority - New priority (urgent, high, medium, low)
   * @returns {Promise<Object>} Updated case
   */
  async updatePriority(caseId, priority) {
    try {
      const response = await api.patch(`/cases/${caseId}`, { priority });
      return response.data;
    } catch (error) {
      console.error(`Error updating case priority ${caseId}:`, error);
      throw error;
    }
  }

  // Results call queue. These return the JSON body ({ success, data, error }).
  getCallQueue(params = {}) {
    return api.get('/cases/call-queue', params);
  }

  addToCallQueue(patientIds, { allowMissingConsent = false } = {}) {
    return api.post('/cases/call-queue', { patientIds, allowMissingConsent });
  }

  updateCallQueueItem(caseId, action) {
    return api.patch(`/cases/call-queue/${caseId}`, { action });
  }

  sendPreCallText(caseId, message) {
    return api.post(`/cases/call-queue/${caseId}/text`, message ? { message } : {});
  }

  startResultsCall(caseId, { allowMissingConsent = false } = {}) {
    return api.post(`/cases/call-queue/${caseId}/call`, { allowMissingConsent });
  }

  getCallQueueSettings() {
    return api.get('/cases/call-queue/settings');
  }

  updateCallQueueSettings(data) {
    return api.put('/cases/call-queue/settings', data);
  }

  connectInboundNumber() {
    return api.post('/cases/call-queue/inbound/connect', {});
  }

  getCallTranscript(caseId) {
    return api.get(`/cases/call-queue/${caseId}/transcript`);
  }

  takeOverCall(caseId, number) {
    return api.post(`/cases/call-queue/${caseId}/takeover`, number ? { number } : {});
  }

  endResultsCall(caseId) {
    return api.post(`/cases/call-queue/${caseId}/end-call`, {});
  }
}

// Export singleton instance
const caseService = new CaseService();
export default caseService;
